import {FontsController} from '@mintdata-wsp/common/controllers/assets/FontsController';
import {PublishMode} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {GraphicType, IGraphic} from '@mintdata/mint-kit/api/assets/Graphic';
import {
    ExternalFonts,
    FavoriteFonts,
    FontName,
    IFontsState,
    LoadedFonts,
    RecentFonts,
    SystemFonts,
    UsedFonts
} from '@mintdata/mint-kit/api/models/FontItem';
import {observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IDesignGraphicSnapshot
{
    name:string;
    type:GraphicType;
}

export interface IDesignGraphicsStateSnapshot
{
    favoriteGraphics:IDesignGraphicSnapshot[];
    recentGraphics:IDesignGraphicSnapshot[];
}

export interface IDesignFontsStateSnapshot
{
    usedFonts:FontName[];
    recentFonts:FontName[];
    favoriteFonts:FontName[];
}

export interface IDesignUploadedImageSnapshot
{
    name:string;
    label:string;
    url:string;
}

export interface IDesignAssetsStateSnapshot
{
    graphicsState:IDesignGraphicsStateSnapshot;
    fontsState:IDesignFontsStateSnapshot;
    uploadedImages:IDesignUploadedImageSnapshot[];
    publishState:IPublishStateSnapshot;
}

export interface IPublishStateSnapshot
{
    mode:PublishMode;
    urlPart:string;
    betaUrlPart:string;
    isBeta:boolean;
}

export class DesignAssetsState
{
    @observable
    public graphicsState:DesignGraphicsState = null;

    @observable
    public fontsState:DesignFontsState = null;

    @observable
    public uploadedImages:DesignUploadedImage[];

    @observable
    public publishState:PublishState = null;

    private constructor()
    {
    }

    public static create():DesignAssetsState
    {
        const designAssetsState:DesignAssetsState = new DesignAssetsState();

        designAssetsState.graphicsState = DesignGraphicsState.create();
        designAssetsState.fontsState = DesignFontsState.create();
        designAssetsState.uploadedImages = [];
        designAssetsState.publishState = PublishState.create();

        return designAssetsState;
    }

    public static toJson:ITransformer<DesignAssetsState, IDesignAssetsStateSnapshot> = createTransformer((designAssetsState:DesignAssetsState) =>
    {
        return {
            graphicsState: DesignGraphicsState.toJson(designAssetsState.graphicsState),
            fontsState: DesignFontsState.toJson(designAssetsState.fontsState),
            uploadedImages: designAssetsState.uploadedImages.map(DesignUploadedImage.toJson),
            publishState: PublishState.toJson(designAssetsState.publishState)
        };
    });

    public static fromJson(json:IDesignAssetsStateSnapshot,
                           currentState?:DesignAssetsState,
                           currentJson?:IDesignAssetsStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):DesignAssetsState
    {
        if( !currentState )
            return DesignAssetsState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = DesignAssetsState.toJson(currentState);

        const designAssetsState:DesignAssetsState = new DesignAssetsState();

        designAssetsState.graphicsState = json.graphicsState === currentJson.graphicsState
            ? currentState.graphicsState
            : DesignGraphicsState.fromJson(json.graphicsState, currentState.graphicsState, currentJson.graphicsState, ctx);

        designAssetsState.fontsState = json.fontsState === currentJson.fontsState
            ? currentState.fontsState
            : DesignFontsState.fromJson(json.fontsState, currentState.fontsState, currentJson.fontsState, ctx);

        designAssetsState.uploadedImages = json.uploadedImages.map((_, index:number) =>
        {
            return json.uploadedImages[index] === currentJson.uploadedImages[index]
                ? currentState.uploadedImages[index]
                : DesignUploadedImage.fromJson(json.uploadedImages[index]);
        });

        designAssetsState.publishState = json.publishState === currentJson.publishState
            ? currentState.publishState
            : PublishState.fromJson(json.publishState, currentState.publishState, currentJson.publishState, ctx);

        return designAssetsState;
    }

    public static fromJsonDeep(json:IDesignAssetsStateSnapshot):DesignAssetsState
    {
        const designAssetsState:DesignAssetsState = new DesignAssetsState();

        designAssetsState.graphicsState = DesignGraphicsState.fromJsonDeep(json.graphicsState);
        designAssetsState.fontsState = DesignFontsState.fromJsonDeep(json.fontsState);
        designAssetsState.uploadedImages = json.uploadedImages.map(DesignUploadedImage.fromJsonDeep);
        designAssetsState.publishState = PublishState.fromJsonDeep(json.publishState);

        return designAssetsState;
    }
}

export class DesignGraphic
{
    constructor(public name:string,
                public type:GraphicType)
    {
    }

    public static fromGraphic(graphic:IGraphic):DesignGraphic
    {
        const {name, type} = graphic.getRef();

        return new DesignGraphic(name, type);
    }

    public static toJson:ITransformer<DesignGraphic, IDesignGraphicSnapshot> = createTransformer((designGraphic:DesignGraphic) =>
    {
        return {
            name: designGraphic.name,
            type: designGraphic.type
        };
    });

    public static fromJson(json:IDesignGraphicSnapshot):DesignGraphic
    {
        return new DesignGraphic(json.name, json.type);
    }

    public static fromJsonDeep(json:IDesignGraphicSnapshot):DesignGraphic
    {
        return DesignGraphic.fromJson(json);
    }
}

export class DesignGraphicsState
{
    @observable
    public favoriteGraphics:DesignGraphic[] = [];

    @observable
    public recentGraphics:DesignGraphic[] = [];

    private constructor()
    {
    }

    public static create():DesignGraphicsState
    {
        return new DesignGraphicsState();
    }

    public static toJson:ITransformer<DesignGraphicsState, IDesignGraphicsStateSnapshot> = createTransformer((designGraphicsState:DesignGraphicsState) =>
    {
        return {
            favoriteGraphics: designGraphicsState.favoriteGraphics.map(DesignGraphic.toJson),
            recentGraphics: designGraphicsState.recentGraphics.map(DesignGraphic.toJson)
        };
    });

    public static fromJson(json:IDesignGraphicsStateSnapshot,
                           currentState?:DesignGraphicsState,
                           currentJson?:IDesignGraphicsStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):DesignGraphicsState
    {
        if( !currentState )
            return DesignGraphicsState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = DesignGraphicsState.toJson(currentState);

        const designGraphicsState:DesignGraphicsState = new DesignGraphicsState();

        designGraphicsState.favoriteGraphics = json.favoriteGraphics.map((_, index:number) =>
        {
            return json.favoriteGraphics[index] === currentJson.favoriteGraphics[index]
                ? currentState.favoriteGraphics[index]
                : DesignGraphic.fromJson(json.favoriteGraphics[index]);
        });

        designGraphicsState.recentGraphics = json.recentGraphics.map((_, index:number) =>
        {
            return json.recentGraphics[index] === currentJson.recentGraphics[index]
                ? currentState.recentGraphics[index]
                : DesignGraphic.fromJson(json.recentGraphics[index]);
        });

        return designGraphicsState;
    }

    public static fromJsonDeep(json:IDesignGraphicsStateSnapshot):DesignGraphicsState
    {
        const designGraphicsState:DesignGraphicsState = new DesignGraphicsState();

        designGraphicsState.favoriteGraphics = json.favoriteGraphics.map(DesignGraphic.fromJsonDeep);
        designGraphicsState.recentGraphics = json.recentGraphics.map(DesignGraphic.fromJsonDeep);

        return designGraphicsState;
    }
}

export class DesignFontsState implements IFontsState
{
    @observable
    public systemFonts:SystemFonts = FontsController.getSystemFonts();

    @observable
    public externalFonts:ExternalFonts = new Map();

    @observable
    public loadedFonts:LoadedFonts = new Map();

    @observable
    public usedFonts:UsedFonts = new Map();

    @observable
    public recentFonts:RecentFonts = new Map();

    @observable
    public favoriteFonts:FavoriteFonts = new Map();

    private constructor()
    {
    }

    public static create():DesignFontsState
    {
        return new DesignFontsState();
    }

    public static toJson:ITransformer<DesignFontsState, IDesignFontsStateSnapshot> = createTransformer((designFontsState:DesignFontsState) =>
    {
        const _toJson = (fonts:UsedFonts | RecentFonts | FavoriteFonts) =>
            Array.from(fonts.keys()).map((fontName:FontName) => fontName);

        return {
            usedFonts: _toJson(designFontsState.usedFonts),
            recentFonts: _toJson(designFontsState.recentFonts),
            favoriteFonts: _toJson(designFontsState.favoriteFonts),
        };
    });

    public static fromJson(json:IDesignFontsStateSnapshot,
                           currentState?:DesignFontsState,
                           currentJson?:IDesignFontsStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):DesignFontsState
    {
        // console.log('%c json =', 'background:#0f0;color:#000;', json.usedFonts);
        // console.log('%c currentState =', 'background:#080;color:#000;', currentState.usedFonts);
        // console.log('%c currentJson =', 'background:#080;color:#000;', currentJson.usedFonts);

        if( !currentState )
            return DesignFontsState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = DesignFontsState.toJson(currentState);

        const getFontsMap = (fontsJson:string[], fontsState:UsedFonts | RecentFonts | FavoriteFonts) =>
        {
            const fontsMap = new Map();

            fontsJson.forEach((fontName:FontName) =>
            {
                fontsMap.set(fontName, fontsState.has(fontName) ? fontsState.get(fontName) : 1);
            });

            return fontsMap;
        };

        const designFontsState:DesignFontsState = {
            ...currentState,

            usedFonts: json.usedFonts === currentJson.usedFonts
                ? currentState.usedFonts
                : getFontsMap(json.usedFonts, currentState.usedFonts),

            recentFonts: json.recentFonts === currentJson.recentFonts
                ? currentState.recentFonts
                : getFontsMap(json.recentFonts, currentState.recentFonts),

            favoriteFonts: json.favoriteFonts === currentJson.favoriteFonts
                ? currentState.favoriteFonts
                : getFontsMap(json.favoriteFonts, currentState.favoriteFonts),
        };

        return designFontsState;
    }

    public static fromJsonDeep(json:IDesignFontsStateSnapshot):DesignFontsState
    {
        const designFontsState:DesignFontsState = new DesignFontsState();

        designFontsState.usedFonts = new Map();
        json.usedFonts.forEach((fontName:FontName) => designFontsState.usedFonts.set(fontName, 1));

        designFontsState.recentFonts = new Map();
        json.recentFonts.forEach((fontName:FontName) => designFontsState.recentFonts.set(fontName, true));

        designFontsState.favoriteFonts = new Map();
        json.favoriteFonts.forEach((fontName:FontName) => designFontsState.favoriteFonts.set(fontName, true));

        return designFontsState;
    }
}

export class DesignUploadedImage
{
    constructor(public name:string,
                public label:string,
                public url:string)
    {
    }

    public static toJson:ITransformer<DesignUploadedImage, IDesignUploadedImageSnapshot> = createTransformer((designUploadedImage:DesignUploadedImage) =>
    {
        return {
            name: designUploadedImage.name,
            label: designUploadedImage.label,
            url: designUploadedImage.url
        };
    });

    public static fromJson(json:IDesignUploadedImageSnapshot):DesignUploadedImage
    {
        return new DesignUploadedImage(json.name, json.label, json.url);
    }

    public static fromJsonDeep(json:IDesignUploadedImageSnapshot):DesignUploadedImage
    {
        return DesignUploadedImage.fromJson(json);
    }
}

export class PublishState
{
    @observable
    public mode:PublishMode = null;

    @observable
    public urlPart:string = null;

    @observable
    public betaUrlPart:string = null;

    @observable
    public isBeta:boolean = false;

    private constructor(mode:PublishMode,
                        urlPart:string,
                        betaUrlPart:string,
                        isBeta:boolean)
    {
        this.mode = mode;
        this.urlPart = urlPart;
        this.betaUrlPart = betaUrlPart;
        this.isBeta = isBeta;
    }

    public static create():PublishState
    {
        return new PublishState(null, null, null, false);
    }

    public static toJson:ITransformer<PublishState, IPublishStateSnapshot> = createTransformer((publishState:PublishState) =>
    {
        return {
            mode: publishState.mode,
            urlPart: publishState.urlPart,
            betaUrlPart: publishState.betaUrlPart,
            isBeta: publishState.isBeta
        };
    });

    public static fromJson(json:IPublishStateSnapshot,
                           currentState?:PublishState,
                           currentJson?:IPublishStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):PublishState
    {
        if( !currentState )
            return PublishState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = PublishState.toJson(currentState);

        if( currentJson.mode === json.mode
            && currentJson.urlPart === json.urlPart
            && currentJson.betaUrlPart === json.betaUrlPart
            && currentJson.isBeta === json.isBeta )
            return currentState;

        return PublishState.fromJsonDeep(json);
    }

    public static fromJsonDeep(json:IPublishStateSnapshot = {} as any):PublishState
    {
        return new PublishState(
            json.mode,
            json.urlPart,
            json.betaUrlPart,
            json.isBeta
        );
    }
}
